import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import { Settings, Save, Loader2, FileText, Plus, Home, ArrowLeft, RefreshCw } from "lucide-react";
import { useGoogleDrive } from "../hooks/useGoogleDrive";
import { useAuth } from "../contexts/AuthContext";
import InputTab from "./tabs/InputTab";
import OverviewTab from "./tabs/OverviewTab";
import LinkedInTab from "./tabs/LinkedInTab";
import TwitterTab from "./tabs/TwitterTab";
import FacebookTab from "./tabs/FacebookTab";
import VideosTab from "./tabs/VideosTab";
import EmailsTab from "./tabs/EmailsTab";
import PodcastTab from "./tabs/PodcastTab";
import InfographicTab from "./tabs/InfographicTab";
import SEOBlogsTab from "./tabs/SEOBlogsTab";
import BlogIdeasTab from "./tabs/BlogIdeasTab";
import SettingsTab from "./tabs/SettingsTab";
import ProjectsModal from "./modals/ProjectsModal";
import GoogleDriveModal from "./modals/GoogleDriveModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ContentHub() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isAdmin, token } = useAuth();
  
  // Create axios instance with auth headers
  const authAxios = axios.create({
    baseURL: API,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  // Add auth token to all requests
  authAxios.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  // Navigation state.
  // When opening an existing project (projectId in the URL), default straight to
  // the outputs/overview tab instead of the input form. loadProjectById also sets
  // this once the data arrives — initialising here avoids a flash of the input tab.
  const [activeTab, setActiveTab] = useState(projectId ? "overview" : "input");
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeEmail, setActiveEmail] = useState(0);
  const [activeVideo, setActiveVideo] = useState(0);

  // Form state
  const [sourceContent, setSourceContent] = useState("");
  const [contentTitle, setContentTitle] = useState("");
  const [targetAudience, setTargetAudience] = useState("trades");
  const [contentTone, setContentTone] = useState("direct");

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [hasContent, setHasContent] = useState(false);

  // Generated content
  const [generatedContent, setGeneratedContent] = useState({
    linkedin: [],
    twitter: [],
    facebook: [],
    videos: [],
    emails: [],
    podcast: [],
    infographic: [],
    seoblogs: [],
    blog_ideas: []
  });

  // Settings state
  const [settings, setSettings] = useState({
    hasClaude: false,
    hasAirtable: false,
    driveConnected: false,
    googleClientId: ""
  });

  // Current project tracking (for upsert on save)
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Airtable state
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedProjects, setSavedProjects] = useState([]);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Google Drive integration
  const {
    driveConnected,
    driveFiles,
    isLoadingDrive,
    connectGoogleDrive,
    disconnectGoogleDrive,
    loadDriveFiles,
    loadFromGoogleDoc,
    gapiInitialized
  } = useGoogleDrive(settings.googleClientId);

  const [showDriveModal, setShowDriveModal] = useState(false);

  // Restore inputs from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('hub_generation_inputs');
    if (saved) {
      try {
        const { contentTitle: t, sourceContent: s, targetAudience: a, contentTone: tone } = JSON.parse(saved);
        if (t) setContentTitle(t);
        if (s) setSourceContent(s);
        if (a) setTargetAudience(a);
        if (tone) setContentTone(tone);
      } catch (e) {
        // ignore malformed saved data
      }
    }
  }, []);

  // Save inputs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('hub_generation_inputs', JSON.stringify({
      contentTitle, sourceContent, targetAudience, contentTone
    }));
  }, [contentTitle, sourceContent, targetAudience, contentTone]);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Load project if projectId is provided
  useEffect(() => {
    if (projectId) {
      loadProjectById(projectId);
    }
  }, [projectId]);

  const loadProjectById = async (id) => {
    try {
      const response = await authAxios.get(`/projects/${id}`);
      const project = response.data?.project;

      if (project) {
        setCurrentProjectId(project.id);
        setContentTitle(project.title || "");
        setSourceContent(project.source_content || "");
        setTargetAudience(project.target_audience || "trades");
        setContentTone(project.content_tone || "direct");
        setGeneratedContent(project.generated_content || {
          linkedin: [],
          twitter: [],
          facebook: [],
          videos: [],
          emails: [],
          podcast: [],
          infographic: [],
          seoblogs: [],
          blog_ideas: []
        });
        setHasContent(true);
        setActiveTab("overview");
        toast.success("Project loaded successfully!");
      }
    } catch (error) {
      console.error("Load project error:", error);
      toast.error("Failed to load project");
    }
  };

  const loadSettings = async () => {
    try {
      const response = await authAxios.get(`/settings`);
      setSettings({
        hasClaude: response.data?.hasClaude || false,
        hasAirtable: response.data?.hasAirtable || false,
        driveConnected: driveConnected || false,
        googleClientId: response.data?.googleClientId || ""
      });
    } catch (error) {
      console.error("Failed to load settings:", error);
      // Set default values on error
      setSettings({
        hasClaude: false,
        hasAirtable: false,
        driveConnected: false,
        googleClientId: ""
      });
    }
  };

  const generateContent = async () => {
    if (!contentTitle || !sourceContent) {
      toast.error("Please fill in both title and content");
      return;
    }

    if (!settings.hasClaude) {
      toast.error("Please configure Claude API key in Settings first");
      setActiveTab("settings");
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    const contentTypes = ["linkedin", "twitter", "facebook", "videos", "emails", "podcast", "infographic", "seoblogs", "blog_ideas"];
    const newContent = {};
    let hasErrors = false;

    try {
      for (let i = 0; i < contentTypes.length; i++) {
        const type = contentTypes[i];

        try {
          const response = await authAxios.post(`/generate/content`, {
            content_title: contentTitle,
            source_content: sourceContent,
            target_audience: targetAudience,
            content_tone: contentTone,
            content_type: type
          });

          newContent[type] = response.data.content;
          const displayName = type === "seoblogs" ? "SEO Blogs" : type === "blog_ideas" ? "Blog Ideas" : type.charAt(0).toUpperCase() + type.slice(1);
          toast.success(`${displayName} generated!`);
        } catch (error) {
          console.error(`Error generating ${type}:`, error);
          hasErrors = true;
          newContent[type] = [];
          const errorMsg = error.response?.data?.detail || error.message;
          toast.error(`Failed to generate ${type}: ${errorMsg}`);
        }

        setGenerationProgress(Math.round(((i + 1) / contentTypes.length) * 100));

        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setGeneratedContent(newContent);
      setHasContent(true);
      setActiveTab("overview");
      
      if (hasErrors) {
        toast.warning("Some content failed to generate. Check your API key configuration.");
      } else {
        toast.success("All content generated successfully!");
      }

      // Auto-save project to MongoDB
      await autoSaveProject(newContent);
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("Failed to generate content: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  // State for tracking which content type is being regenerated
  const [regeneratingType, setRegeneratingType] = useState(null);

  // State for tracking which individual item is being regenerated
  const [regeneratingItem, setRegeneratingItem] = useState(null); // { type, index }

  // Regenerate a single content type
  const regenerateContent = async (contentType) => {
    if (!sourceContent.trim()) {
      toast.error("No source content available");
      return;
    }

    setRegeneratingType(contentType);
    
    const displayName = contentType === "seoblogs" ? "SEO Blogs" : contentType.charAt(0).toUpperCase() + contentType.slice(1);
    
    try {
      const response = await authAxios.post(`/generate/content`, {
        content_title: contentTitle,
        source_content: sourceContent,
        target_audience: targetAudience,
        content_tone: contentTone,
        content_type: contentType
      });

      setGeneratedContent(prev => ({
        ...prev,
        [contentType]: response.data.content
      }));
      
      toast.success(`${displayName} regenerated!`);

      // Auto-save with updated content
      const updatedContent = {
        ...generatedContent,
        [contentType]: response.data.content
      };
      await autoSaveProject(updatedContent);
      
    } catch (error) {
      console.error(`Error regenerating ${contentType}:`, error);
      toast.error(`Failed to regenerate ${displayName}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRegeneratingType(null);
    }
  };

  // Regenerate a single item within a content type
  const handleRegenerateItem = async (contentType, itemIndex) => {
    if (!sourceContent.trim()) {
      toast.error("No source content available");
      return;
    }

    setRegeneratingItem({ type: contentType, index: itemIndex });

    try {
      const response = await authAxios.post(`/generate/content`, {
        content_title: contentTitle,
        source_content: sourceContent,
        target_audience: targetAudience,
        content_tone: contentTone,
        content_type: contentType
      });

      const freshArray = response.data.content;
      const newItem = freshArray[itemIndex] ?? freshArray[0];

      setGeneratedContent(prev => {
        const updated = [...(prev[contentType] || [])];
        updated[itemIndex] = newItem;
        return { ...prev, [contentType]: updated };
      });

      toast.success("Item regenerated");
    } catch (err) {
      toast.error(`Regenerate failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRegeneratingItem(null);
    }
  };

  const autoSaveProject = async (content, projectId = currentProjectId) => {
    const payload = {
      project_title: contentTitle,
      source_content: sourceContent,
      target_audience: targetAudience,
      content_tone: contentTone,
      generated_content: content
    };
    try {
      if (projectId) {
        await authAxios.put(`/projects/${projectId}`, payload);
      } else {
        const response = await authAxios.post(`/projects/save`, payload);
        const newId = response.data?.project_id;
        if (newId) setCurrentProjectId(newId);
      }
      localStorage.removeItem('hub_generation_inputs');
      toast.success("Project auto-saved!");
    } catch (error) {
      console.error("Auto-save error:", error);
      toast.error("Auto-save failed — your content may not have been saved.");
    }
  };

  const useMockData = () => {
    setGeneratedContent(mockGeneratedContent);
    setHasContent(true);
    setActiveTab("overview");
    toast.success("Loaded sample content!");
  };

  const saveToAirtable = async () => {
    if (!settings.hasAirtable) {
      toast.error("Please configure Airtable in Settings first");
      setActiveTab("settings");
      return;
    }

    if (!hasContent) {
      toast.error("No content to save. Generate content first.");
      return;
    }

    setIsSaving(true);
    setSaveStatus("saving");

    try {
      const response = await authAxios.post(`/airtable/save-project`, {
        project_title: contentTitle,
        source_content: sourceContent,
        target_audience: targetAudience,
        content_tone: contentTone,
        generated_content: generatedContent,
        created_at: new Date().toISOString()
      });

      setSaveStatus("saved");
      toast.success("Project saved to Airtable!");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Save error:", error);
      setSaveStatus("error");
      toast.error("Failed to save to Airtable");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const loadProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const response = await authAxios.get(`/projects/`);
      setSavedProjects(response.data.projects || []);
      setShowProjectsModal(true);
    } catch (error) {
      console.error("Load projects error:", error);
      toast.error("Failed to load projects");
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadProject = async (projectId) => {
    setShowProjectsModal(false);
    await loadProjectById(projectId);
  };

  const handleConnectDrive = async () => {
    if (!gapiInitialized) {
      toast.error("Please configure Google Client ID in Settings first");
      setActiveTab("settings");
      return;
    }
    await connectGoogleDrive();
  };

  const handleLoadDriveFiles = async () => {
    if (!driveConnected) {
      await handleConnectDrive();
      return;
    }
    await loadDriveFiles();
    setShowDriveModal(true);
  };

  const handleLoadDriveFile = async (fileId, fileName) => {
    const result = await loadFromGoogleDoc(fileId, fileName);
    if (result) {
      setSourceContent(result.content);
      setContentTitle(result.title);
      setShowDriveModal(false);
    }
  };

  const handleDriveSearch = async (query) => {
    await loadDriveFiles(query);
  };

  const resetForm = () => {
    setSourceContent("");
    setContentTitle("");
    setTargetAudience("trades");
    setContentTone("direct");
    setCurrentProjectId(null);
    setGeneratedContent({
      linkedin: [],
      twitter: [],
      facebook: [],
      videos: [],
      emails: [],
      podcast: [],
      infographic: [],
      seoblogs: [],
      blog_ideas: []
    });
    setHasContent(false);
    setActiveTab("input");
    setActiveSlide(0);
    setActiveEmail(0);
    setActiveVideo(0);
  };

  const tabs = hasContent
    ? isAdmin 
      ? ["overview", "linkedin", "twitter", "facebook", "videos", "emails", "podcast", "infographic", "seoblogs", "blog ideas", "settings"]
      : ["overview", "linkedin", "twitter", "facebook", "videos", "emails", "podcast", "infographic", "seoblogs", "blog ideas"]
    : isAdmin 
      ? ["input", "settings"]
      : ["input"];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Back to Dashboard</span>
          </Button>
          <div className="h-8 w-px bg-white/10" />
          <div>
            <div className="font-mono text-[#00d4aa] text-xs uppercase tracking-wider mb-1">
              EPSILLON MEDIA
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-white">
              Content Repurposing Hub
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {hasContent && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={loadProjects}
                className="hidden md:flex"
              >
                <FileText className="w-4 h-4 mr-2" />
                Load Project
              </Button>

              {saveStatus === "saving" && (
                <div className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Saving...</span>
                </div>
              )}
              {saveStatus === "saved" && (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                  ✓ Saved
                </div>
              )}
              {saveStatus === "error" && (
                <div className="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                  ✗ Failed
                </div>
              )}

              <Button
                onClick={saveToAirtable}
                disabled={isSaving}
                className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save to Airtable
              </Button>
            </>
          )}

          {hasContent && contentTitle && (
            <div className="hidden md:block px-3 py-1 bg-white/5 rounded-lg text-sm text-white/60">
              {contentTitle}
            </div>
          )}

          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab("settings")}
              className="text-white/60 hover:text-white"
            >
              <Settings className="w-5 h-5" />
            </Button>
          )}
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="px-4 md:px-8 py-4 border-b border-white/10">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {hasContent && (
            <Button
              variant="outline"
              size="sm"
              onClick={resetForm}
              className="whitespace-nowrap border-[#00d4aa]/30 text-[#00d4aa]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New
            </Button>
          )}
          
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`tab-button ${
                activeTab === tab ? "active" : "inactive"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
        {activeTab === "input" && (
          <InputTab
            contentTitle={contentTitle}
            setContentTitle={setContentTitle}
            sourceContent={sourceContent}
            setSourceContent={setSourceContent}
            targetAudience={targetAudience}
            setTargetAudience={setTargetAudience}
            contentTone={contentTone}
            setContentTone={setContentTone}
            isGenerating={isGenerating}
            generationProgress={generationProgress}
            generateContent={generateContent}
            useMockData={useMockData}
            settings={settings}
            connectGoogleDrive={handleConnectDrive}
            loadProjects={loadProjects}
            loadDriveFiles={handleLoadDriveFiles}
            driveConnected={driveConnected}
          />
        )}

        {activeTab === "overview" && (
          <OverviewTab
            generatedContent={generatedContent}
            setActiveTab={setActiveTab}
          />
        )}

        {activeTab === "linkedin" && (
          <LinkedInTab
            content={generatedContent.linkedin}
            activeSlide={activeSlide}
            setActiveSlide={setActiveSlide}
            onRegenerate={() => regenerateContent('linkedin')}
            isRegenerating={regeneratingType === 'linkedin'}
            onRegenerateItem={(index) => handleRegenerateItem('linkedin', index)}
            regeneratingItem={regeneratingItem?.type === 'linkedin' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "twitter" && (
          <TwitterTab
            content={generatedContent.twitter}
            onRegenerate={() => regenerateContent('twitter')}
            isRegenerating={regeneratingType === 'twitter'}
            onRegenerateItem={(index) => handleRegenerateItem('twitter', index)}
            regeneratingItem={regeneratingItem?.type === 'twitter' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "facebook" && (
          <FacebookTab
            content={generatedContent.facebook}
            onRegenerate={() => regenerateContent('facebook')}
            isRegenerating={regeneratingType === 'facebook'}
            onRegenerateItem={(index) => handleRegenerateItem('facebook', index)}
            regeneratingItem={regeneratingItem?.type === 'facebook' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "videos" && (
          <VideosTab
            content={generatedContent.videos}
            activeVideo={activeVideo}
            setActiveVideo={setActiveVideo}
            onRegenerate={() => regenerateContent('videos')}
            isRegenerating={regeneratingType === 'videos'}
            onRegenerateItem={(index) => handleRegenerateItem('videos', index)}
            regeneratingItem={regeneratingItem?.type === 'videos' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "emails" && (
          <EmailsTab
            content={generatedContent.emails}
            activeEmail={activeEmail}
            setActiveEmail={setActiveEmail}
            onRegenerate={() => regenerateContent('emails')}
            isRegenerating={regeneratingType === 'emails'}
            onRegenerateItem={(index) => handleRegenerateItem('emails', index)}
            regeneratingItem={regeneratingItem?.type === 'emails' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "podcast" && (
          <PodcastTab
            content={generatedContent.podcast}
            onRegenerate={() => regenerateContent('podcast')}
            isRegenerating={regeneratingType === 'podcast'}
            onRegenerateItem={(index) => handleRegenerateItem('podcast', index)}
            regeneratingItem={regeneratingItem?.type === 'podcast' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "infographic" && (
          <InfographicTab
            content={generatedContent.infographic}
            onRegenerate={() => regenerateContent('infographic')}
            isRegenerating={regeneratingType === 'infographic'}
            onRegenerateItem={(index) => handleRegenerateItem('infographic', index)}
            regeneratingItem={regeneratingItem?.type === 'infographic' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "seoblogs" && (
          <SEOBlogsTab
            content={generatedContent.seoblogs}
            onRegenerate={() => regenerateContent('seoblogs')}
            isRegenerating={regeneratingType === 'seoblogs'}
            onRegenerateItem={(index) => handleRegenerateItem('seoblogs', index)}
            regeneratingItem={regeneratingItem?.type === 'seoblogs' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "blog ideas" && (
          <BlogIdeasTab
            content={generatedContent.blog_ideas}
            onRegenerate={() => regenerateContent('blog_ideas')}
            isRegenerating={regeneratingType === 'blog_ideas'}
            onRegenerateItem={(index) => handleRegenerateItem('blog_ideas', index)}
            regeneratingItem={regeneratingItem?.type === 'blog_ideas' ? regeneratingItem.index : null}
          />
        )}

        {activeTab === "settings" && (
          <SettingsTab 
            settings={settings} 
            loadSettings={loadSettings}
            driveConnected={driveConnected}
            disconnectDrive={disconnectGoogleDrive}
          />
        )}
      </main>

      {/* Modals */}
      <ProjectsModal
        isOpen={showProjectsModal}
        onClose={() => setShowProjectsModal(false)}
        projects={savedProjects}
        isLoading={isLoadingProjects}
        onLoadProject={loadProject}
      />

      <GoogleDriveModal
        isOpen={showDriveModal}
        onClose={() => setShowDriveModal(false)}
        files={driveFiles}
        isLoading={isLoadingDrive}
        onLoadFile={handleLoadDriveFile}
        onSearch={handleDriveSearch}
        driveConnected={driveConnected}
        onConnect={handleConnectDrive}
      />
    </div>
  );
}

export default ContentHub;
